import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { fetchPublicAvailability, createPublicBooking, schedulingKeys } from '../../api/scheduling';
import { TurnstileWidget } from '../../components/TurnstileWidget';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';
import { ApiRequestError } from '../../api/client';

function turnstileSiteKey(): string {
  return import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
}

const BOOKING_RANGE_DAYS = 14;
const RANGE_START = Math.floor(Date.now() / 1000);
const RANGE_END = RANGE_START + BOOKING_RANGE_DAYS * 24 * 60 * 60;

function formatDate(epoch: number): string {
  return new Date(epoch * 1000).toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(epoch: number): string {
  return new Date(epoch * 1000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

type BookingStep = 'slots' | 'details' | 'submitting' | 'done' | 'error';

export function BookingPage(): React.JSX.Element {
  const { slug } = useParams<'slug'>();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<BookingStep>('slots');
  const [selectedSlot, setSelectedSlot] = useState<{ startsAt: number; endsAt: number } | null>(
    null
  );
  const [contactName, setContactName] = useState('');
  const [phoneE164, setPhoneE164] = useState('');
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const availabilityQuery = useQuery({
    queryKey: schedulingKeys.availability.slots(`book-${slug!}`, RANGE_START, RANGE_END),
    queryFn: ({ signal }) => fetchPublicAvailability(slug!, RANGE_START, RANGE_END, signal),
    enabled: slug !== undefined,
  });

  const bookingMutation = useMutation({
    mutationFn: () => {
      if (selectedSlot === null) throw new Error('No slot selected');
      return createPublicBooking(slug!, {
        contactName: contactName.trim(),
        phoneE164: phoneE164.trim(),
        ...(email.trim().length > 0 ? { email: email.trim() } : {}),
        slotStartsAt: selectedSlot.startsAt,
        turnstileToken: turnstileToken ?? '',
      });
    },
    onSuccess: () => {
      setStep('done');
      void queryClient.invalidateQueries({ queryKey: ['appointments', `book-${slug!}`] });
    },
    onError: (error: unknown) => {
      setSubmitError(error);
      if (error instanceof ApiRequestError && error.code === 'conflict') {
        setStep('error');
      }
    },
  });

  if (slug === undefined) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <p className="text-sm text-slate-500">Invalid booking link.</p>
      </main>
    );
  }

  const handleSlotSelected = (startsAt: number, endsAt: number) => {
    setSelectedSlot({ startsAt, endsAt });
    setStep('details');
    setFieldError(null);
    setSubmitError(null);
  };

  const handleSubmitDetails = (event: FormEvent) => {
    event.preventDefault();
    setFieldError(null);
    setSubmitError(null);

    if (contactName.trim().length === 0) {
      setFieldError('Name is required.');
      return;
    }
    if (phoneE164.trim().length === 0) {
      setFieldError('Phone number is required.');
      return;
    }
    if (turnstileToken === null) {
      setFieldError('Complete the verification challenge.');
      return;
    }

    bookingMutation.mutate();
  };

  const timeSlots = availabilityQuery.data?.slots ?? [];

  const groupedByDate: Record<string, typeof timeSlots> = {};
  for (const slot of timeSlots) {
    const dateKey = new Date(slot.startsAt * 1000).toLocaleDateString();
    if (groupedByDate[dateKey] === undefined) {
      groupedByDate[dateKey] = [];
    }
    groupedByDate[dateKey].push(slot);
  }

  return (
    <main className="flex min-h-screen items-start justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-lg">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Book an appointment
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Choose a time slot and we will confirm your appointment.
        </p>

        {step === 'done' && (
          <div
            className="mt-6 rounded-lg border border-green-200 bg-green-50 p-6"
            data-testid="booking-success"
          >
            <h2 className="text-lg font-semibold text-green-900">Appointment booked!</h2>
            <p className="mt-2 text-sm text-green-700">
              Your appointment has been confirmed. You will receive a confirmation shortly.
            </p>
          </div>
        )}

        {step === 'error' && (
          <div className="mt-6" data-testid="booking-error">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
              <h2 className="text-lg font-semibold text-amber-900">Slot no longer available</h2>
              <p className="mt-2 text-sm text-amber-700">
                Sorry, that time slot was just taken. Please choose another.
              </p>
            </div>
            {submitError !== null && (
              <div className="mt-3">
                <ErrorNotice error={submitError} />
              </div>
            )}
            <button
              type="button"
              onClick={() => setStep('slots')}
              className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Choose a different slot
            </button>
          </div>
        )}

        {step === 'slots' && (
          <div className="mt-6">
            {availabilityQuery.isPending && <LoadingScreen />}
            {availabilityQuery.isError && <ErrorNotice error={availabilityQuery.error} />}

            {availabilityQuery.isSuccess && timeSlots.length === 0 && (
              <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
                <p className="text-sm text-slate-500">
                  No available appointments in the next {BOOKING_RANGE_DAYS} days.
                </p>
              </div>
            )}

            {availabilityQuery.isSuccess && timeSlots.length > 0 && (
              <div className="space-y-4">
                {Object.entries(groupedByDate).map(([dateKey, daySlots]) => (
                  <div
                    key={dateKey}
                    className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <h3
                      className="text-sm font-semibold text-slate-700"
                      data-testid="booking-date-group"
                    >
                      {formatDate(daySlots[0]!.startsAt)}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {daySlots.map((slot) => (
                        <button
                          key={slot.startsAt}
                          type="button"
                          onClick={() => handleSlotSelected(slot.startsAt, slot.endsAt)}
                          className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900"
                          data-testid={`book-slot-${slot.startsAt}`}
                        >
                          {formatTime(slot.startsAt)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 'details' && (
          <div className="mt-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            {selectedSlot !== null && (
              <div className="mb-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <span className="font-medium">{formatDate(selectedSlot.startsAt)}</span>
                {' at '}
                <span className="font-medium">{formatTime(selectedSlot.startsAt)}</span>
                {' ('}
                {Math.round((selectedSlot.endsAt - selectedSlot.startsAt) / 60)}
                {' min)'}
              </div>
            )}

            <form onSubmit={handleSubmitDetails} className="space-y-4" noValidate>
              <div>
                <label htmlFor="booking-name" className="block text-sm font-medium text-slate-700">
                  Name *
                </label>
                <input
                  id="booking-name"
                  type="text"
                  autoComplete="name"
                  required
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="booking-phone" className="block text-sm font-medium text-slate-700">
                  Phone *
                </label>
                <input
                  id="booking-phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  value={phoneE164}
                  onChange={(e) => setPhoneE164(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="booking-email" className="block text-sm font-medium text-slate-700">
                  Email (optional)
                </label>
                <input
                  id="booking-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
                />
              </div>

              <TurnstileWidget siteKey={turnstileSiteKey()} onToken={setTurnstileToken} />

              {fieldError !== null && <p className="text-sm text-red-600">{fieldError}</p>}

              {submitError !== null &&
                !(submitError instanceof ApiRequestError && submitError.code === 'conflict') && (
                  <ErrorNotice error={submitError} />
                )}

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={bookingMutation.isPending}
                  className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="book-submit-btn"
                >
                  {bookingMutation.isPending ? 'Booking…' : 'Confirm booking'}
                </button>
                <button
                  type="button"
                  onClick={() => setStep('slots')}
                  className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  Back
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </main>
  );
}
